import type { DefaultPartialBlock } from "../types";

/**
 * 글자 크기(fontSize) 직렬화 호환 레이어.
 *
 * 배경:
 *  - BlockNote는 인라인 `styles` 맵에 스키마에 없는 키가 있으면
 *    `style ${name} not found in styleSchema` 예외를 던진다(blockToNode →
 *    styledTextToNodes). 이미 배포된 구버전 SDK(≤0.4.15, fontSize 스펙 없음)가
 *    `styles.fontSize`가 포함된 JSON을 initialContent로 받으면 에디터 생성이
 *    실패하며 React 트리가 크래시한다.
 *  - 반면 styled-text 객체의 **형제(sibling) 키**는 BlockNote가 읽지 않으므로
 *    조용히 무시된다(크래시 없음).
 *
 * 전략:
 *  - 저장/전달용 JSON(onContentChange 출력)에서는 `styles.fontSize`를 형제 키
 *    `fontSize`로 내린다(lowerFontSize).
 *  - 로드 시(initialContent)에는 형제 키를 다시 `styles.fontSize`로 올려
 *    신버전 에디터가 렌더링하게 한다(liftFontSize).
 *
 * ⚠️ 불변 규칙: 에디터 외부로 블록 JSON을 내보내는 모든 경로는 반드시
 * lowerFontSize를 거쳐야 한다(현재 출력 표면은 onContentChange 유일).
 * styles.fontSize가 저장 JSON에 유출되면 구버전 소비 앱이 크래시한다.
 */

/**
 * 직렬화(저장) 포맷의 styled-text 항목.
 * fontSize는 styles 맵이 아닌 형제 키로 존재한다 — 구버전 SDK(≤0.4.15)는
 * styles만 순회하므로 이 키를 조용히 무시한다(파싱 오류 없음).
 */
export type SerializedStyledText = {
  type: "text";
  text: string;
  styles: Record<string, unknown>;
  /** 인라인 글자 크기(예: "18px"). 구버전 SDK에서는 무시됨 */
  fontSize?: string;
};

type Dir = "lower" | "lift";

/**
 * 배열 map 후 모든 요소가 원본과 동일하면 원본 배열 참조를 그대로 반환
 * (불필요한 재생성 방지 — table-vertical-alignment.ts의 불변 패턴 준수)
 */
function mapPreservingRef<T>(arr: T[], fn: (item: T) => T): T[] {
  let changed = false;
  const next = arr.map((item) => {
    const mapped = fn(item);
    if (mapped !== item) changed = true;
    return mapped;
  });
  return changed ? next : arr;
}

/** styled-text 한 항목의 fontSize를 styles ↔ 형제 키 간 이동 */
function mapStyledItem(item: any, dir: Dir): any {
  if (!item || typeof item !== "object" || item.type !== "text") {
    return item;
  }

  if (dir === "lower") {
    const fs = item.styles?.fontSize;
    if (fs == null) return item;
    const { fontSize: _removed, ...restStyles } = item.styles;
    return { ...item, styles: restStyles, fontSize: fs };
  }

  // lift: 형제 키 → styles.fontSize (둘 다 있으면 형제 키 우선)
  const fs = item.fontSize;
  if (fs == null) return item;
  const { fontSize: _lifted, ...rest } = item;
  return { ...rest, styles: { ...(item.styles ?? {}), fontSize: fs } };
}

/** 인라인 콘텐츠 항목 처리 — link는 중첩 content(StyledText[])를 재귀 */
function mapInlineItem(node: any, dir: Dir): any {
  if (!node || typeof node !== "object") return node;
  if (node.type === "link" && Array.isArray(node.content)) {
    const content = mapPreservingRef(node.content, (n) =>
      mapStyledItem(n, dir),
    );
    return content === node.content ? node : { ...node, content };
  }
  return mapStyledItem(node, dir);
}

/** 테이블 셀 — `{type:"tableCell", content}` 객체와 평문 인라인 배열 두 형태 모두 처리 */
function mapCell(cell: any, dir: Dir): any {
  if (cell && typeof cell === "object" && cell.type === "tableCell") {
    if (!Array.isArray(cell.content)) return cell;
    const content = mapPreservingRef(cell.content, (n) =>
      mapInlineItem(n, dir),
    );
    return content === cell.content ? cell : { ...cell, content };
  }
  if (Array.isArray(cell)) {
    return mapPreservingRef(cell, (n) => mapInlineItem(n, dir));
  }
  return cell;
}

function mapBlock(block: any, dir: Dir): any {
  if (!block || typeof block !== "object") return block;

  let next = block;
  const content = block.content;

  if (Array.isArray(content)) {
    // paragraph/heading/list 계열 인라인 콘텐츠
    const mapped = mapPreservingRef(content, (n) => mapInlineItem(n, dir));
    if (mapped !== content) next = { ...next, content: mapped };
  } else if (
    content &&
    typeof content === "object" &&
    content.type === "tableContent" &&
    Array.isArray(content.rows)
  ) {
    const rows = mapPreservingRef(content.rows, (row: any) => {
      if (!row || !Array.isArray(row.cells)) return row;
      const cells = mapPreservingRef(row.cells, (cell: any) =>
        mapCell(cell, dir),
      );
      return cells === row.cells ? row : { ...row, cells };
    });
    if (rows !== content.rows) next = { ...next, content: { ...content, rows } };
  }
  // content === undefined (image/video/htmlPreview/linkPreview) → 그대로 통과

  if (Array.isArray(block.children) && block.children.length > 0) {
    const children = mapPreservingRef(block.children, (b: any) =>
      mapBlock(b, dir),
    );
    if (children !== block.children) next = { ...next, children };
  }

  return next;
}

/**
 * 출력 방향: `styles.fontSize` → 형제 키 `fontSize`.
 * onContentChange로 내보내는 블록 JSON에 적용해 구버전 SDK 크래시를 방지한다.
 * 입력을 변형하지 않는다(불변).
 */
export function lowerFontSize(
  blocks: DefaultPartialBlock[],
): DefaultPartialBlock[] {
  return mapPreservingRef(blocks as any[], (b) => mapBlock(b, "lower"));
}

/**
 * 입력 방향: 형제 키 `fontSize` → `styles.fontSize`.
 * initialContent를 에디터에 넘기기 전에 적용해 글자 크기를 복원한다.
 * 입력을 변형하지 않는다(불변).
 */
export function liftFontSize(
  blocks: DefaultPartialBlock[],
): DefaultPartialBlock[] {
  return mapPreservingRef(blocks as any[], (b) => mapBlock(b, "lift"));
}
